import React, { useEffect, useState } from "react";
import { Col, Row, Modal, Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  getBookingByID,
  sendSMS,
  getCurrentUserDetails,
  getInquiryDataById,
  getUserByID,
  getListingByID,
  sendEmail,
  getWaiverDataByName,
  getWaiverDescriptionByName,
  tripStatusUpdateEmailBody,
  getListingByBookingId,
  getSecurityAmountCents,
  recordOffPlatformDeposit,
} from "../utils";
import { notifyOwnerTripStarted } from "../utils/userNotifications";
import {
  get,
  getDatabase,
  push,
  ref,
  serverTimestamp,
  set,
  update,
} from "firebase/database";
import SignatureCanvas from "react-signature-canvas";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Alert, Checkbox, FormControlLabel } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import { useAuth } from "../../contexts/AuthContext";

export default function StartTrip() {
  const { dynamicId } = useParams();
  const { token } = useAuth();
  const elements = useElements();
  const stripe = useStripe();
  const navigate = useNavigate();

  const [acknowledgement, setAcknowledgement] = useState(false);

  const [signatureUrl, setSignatureFile] = useState("");
  const [boatType, setBoatType] = useState("");

  const [formResponses, setFormResponses] = useState([]);
  const [adultFormResponses, setAdultFormResponses] = useState([]);
  const [minorFormResponses, setMinorFormResponses] = useState([]);
  const [waiverQuestions, setWaiverQuestions] = useState([]);
  const [adultWaiverQuestions, setAdultWaiverQuestions] = useState([]);
  const [minorWaiverQuestions, setMinorWaiverQuestions] = useState([]);
  const [bookingData, setBookingData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [signingWaiverName, setSigningWaiverName] = useState([]);
  const [waiverDescription, setWaiverDescription] = useState([]);
  const [inquiryDetails, setInquiryDetails] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isCaptain, setIsCaptain] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [listing, setListing] = useState([]);
  const [selectedBoatDetails, setSelectedBoatDetails] = useState({});
  const [showPreStartTripGuidanceModal, setShowPreStartTripGuidanceModal] =
    useState(false);

  const signatureRefs = React.useRef({});
  const [signatureDateTimes, setSignatureDateTimes] = useState({});

  const handleSignatureBegin = (questionId) => {
    setSignatureDateTimes((prev) => {
      if (prev[questionId]) {
        return prev;
      }

      const now = new Date();
      return {
        ...prev,
        [questionId]: now.toLocaleString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
      };
    });
  };

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768; // Adjust the breakpoint as needed

      setIsMobile(isMobile);
    };

    // Initial setup
    handleResize();

    // Attach event listener for window resize
    window.addEventListener("resize", handleResize);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleImageUpload = async (question, files) => {
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/upload-images`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        const filenames = data.file_names;
        const isSignatureQuestion =
          waiverQuestions.find((q) => q.id === question)?.type === "signature";
        const answer = isSignatureQuestion
          ? `${process.env.REACT_APP_BASE_URL}/images/` + filenames
          : filenames;
        const responseData = {
          Question: question,
          Answer: answer,
          ...(isSignatureQuestion ? { type: "signature" } : {}),
        };
        console.log(question);
        if (isSignatureQuestion) {
          setSignatureFile(answer);
          console.log(answer);
        }

        setFormResponses((prevResponses) =>
          mergeUploadedResponse(prevResponses, responseData)
        );

        return responseData;
      } else {
        console.error("Error uploading images:", response.statusText);
        throw new Error("Error uploading images");
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      throw error;
    }
  };

  const mergeUploadedResponse = (responses, uploadedResponse) => {
    if (!uploadedResponse) {
      return responses;
    }

    const updatedResponses = [...responses];
    const existingResponseIndex = updatedResponses.findIndex(
      (response) => response.Question === uploadedResponse.Question
    );

    if (existingResponseIndex !== -1) {
      updatedResponses[existingResponseIndex] = uploadedResponse;
    } else {
      updatedResponses.push(uploadedResponse);
    }

    return updatedResponses;
  };

  const handleSaveSignatures = async () => {
    const signatureQuestions = waiverQuestions.filter((question) => {
      if (question.type !== "signature") {
        return false;
      }

      if (boatType !== "Bass Boat" && question.showField === false) {
        return false;
      }

      if (isCaptain === true && question.showField === false) {
        return false;
      }

      return true;
    });
    const signatureResponses = [];

    for (const question of signatureQuestions) {
      const signatureRef = signatureRefs.current[question.id];

      if (!signatureRef || signatureRef.isEmpty()) {
        throw new Error(`Signature is empty: ${question.question}`);
      }

      const signatureImage = signatureRef
        .getTrimmedCanvas()
        .toDataURL("image/png");
      const signatureFile = dataURLtoFile(signatureImage, "signature.png");
      const response = await handleImageUpload(question.id, [signatureFile]);
      signatureResponses.push(response);
    }

    return signatureResponses;
  };

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  useEffect(() => {
    const todayDateElement = document.querySelector(".today-date");
    if (todayDateElement) {
      const todaysDate = new Date().toLocaleDateString(); // Get today's date in a readable format
      todayDateElement.textContent = `Today's Date: ${todaysDate}`;
    }
  }, [waiverDescription]);

  const checkedQuestions = [];

  const handleInputChange = (question, answer) => {
    if (question === "date" || question === "country") {
      // Handle date and country inputs
      const updatedResponses = [...formResponses];
      const existingResponseIndex = updatedResponses.findIndex(
        (response) => response.Question === question
      );

      if (existingResponseIndex !== -1) {
        updatedResponses[existingResponseIndex].Answer = answer;
      } else {
        updatedResponses.push({ Question: question, Answer: answer });
      }

      setFormResponses(updatedResponses);
    } else {
      let updatedResponses = [...formResponses];
      const existingResponseIndex = updatedResponses.findIndex(
        (response) => response.Question === question
      );

      if (existingResponseIndex !== -1) {
        updatedResponses[existingResponseIndex].Answer = answer;
      } else {
        updatedResponses.push({ Question: question, Answer: answer });
      }

      // When "All Good" is selected, auto-set all subsequent radio questions with "No" option to "No"
      if (answer === "All Good") {
        waiverQuestions.forEach((q) => {
          if (
            q.id > question &&
            q.options &&
            q.options.includes("No")
          ) {
            const idx = updatedResponses.findIndex(
              (r) => r.Question === q.id
            );
            if (idx !== -1) {
              updatedResponses[idx].Answer = "No";
            } else {
              updatedResponses.push({ Question: q.id, Answer: "No" });
            }
          }
        });
      }

      setFormResponses(updatedResponses);
      // add images input
      if (
        answer === "Checked" ||
        answer === `There's an issue` ||
        answer === `Yes`
      ) {
        if (!checkedQuestions.includes(question)) {
          const newImageQuestion = {
            question: "Picture",
            type: "images",
            id: question + 1,
            showField: true,
          };
          const updatedWaiverQuestions = [
            ...waiverQuestions.slice(0, question + 1),
            newImageQuestion,
            ...waiverQuestions.slice(question + 1),
          ];
          const shiftedWaiverQuestions = updatedWaiverQuestions.map(
            (item, index) => ({ ...item, id: index })
          );

          setWaiverQuestions(shiftedWaiverQuestions);
          checkedQuestions.push(question);
          console.log(checkedQuestions);
        }
      } else {
        // Remove the image question if the option is unchecked
        const questionIndex = question;

        console.log("cancelled");
        if (
          questionIndex !== -1 &&
          questionIndex < waiverQuestions.length - 1
        ) {
          const nextQuestion = waiverQuestions[questionIndex + 1].question;
          console.log("nextQuestion");
          console.log(nextQuestion);

          // Check if the next question is not "Please take a picture of it"
          if (nextQuestion == "Picture") {
            // Remove the image question by updating waiverQuestions
            const updatedWaiverQuestions = waiverQuestions.filter(
              (item, index) => index !== questionIndex + 1
            );
            const shiftedWaiverQuestions = updatedWaiverQuestions.map(
              (item, index) => ({ ...item, id: index })
            );
            setWaiverQuestions(shiftedWaiverQuestions);
          }
        }
      }
    }
  };

  const [error, setError] = useState("");

  const requiresStripeHold = !isCaptain && !isAdmin;
  const preStartTripGuidanceStorageKey = `preStartTripGuidanceSeen_${dynamicId}`;

  useEffect(() => {
    if (!bookingData?.listingId || !userData?.userId) {
      return;
    }

    if (
      requiresStripeHold &&
      !localStorage.getItem(preStartTripGuidanceStorageKey)
    ) {
      setShowPreStartTripGuidanceModal(true);
    }
  }, [
    bookingData?.listingId,
    userData?.userId,
    requiresStripeHold,
    preStartTripGuidanceStorageKey,
  ]);

  const closePreStartTripGuidanceModal = () => {
    localStorage.setItem(preStartTripGuidanceStorageKey, "1");
    setShowPreStartTripGuidanceModal(false);
  };

  const handleNotYetFromGuidance = () => {
    setShowPreStartTripGuidanceModal(false);
    navigate(`/dashboard/event/${dynamicId}`);
  };

  const holdPayment = async () => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    let result = {};
    if (!stripe || !elements) {
      return;
    }

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement),
    });

    if (error) {
      console.error(error);
      // Throw an error to stop further execution
      setError("Oops... something went wrong.");
      throw new Error("Error creating payment method");
    }

    try {
      const response = await fetch(`${BASE_URL}/payment-hold`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: getSecurityAmountCents(selectedBoatDetails?.boatType),
          payment_method: paymentMethod.id,
        }),
      });

      result = await response.json();
      console.log("result: ", result);

      if (!result.error) {
        const database = getDatabase();
        const paymentRef = ref(database, `payments/${bookingData.paymentId}`);

        // Retrieve existing data
        const currentData = (await get(paymentRef)).val() || {};

        const paymentHeld = {
          id: result.paymentIntent.id,
          amount: result.paymentIntent.amount,
          status: "pending",
        };

        // Merge existing data with new data
        const mergedData = {
          ...currentData,
          paymentHeld: paymentHeld,
        };

        // update the database with merged data
        await update(paymentRef, mergedData);
        setError("");
      } else {
        setError(result.error);
        setLoading(false);
        throw new Error("Error capturing payment");
      }
    } catch (error) {
      setError(result.error);
      setLoading(false);
      throw new Error("Error capturing payment down");
    }
  };

  useEffect(() => {
    console.log(formResponses);
  }, [formResponses]);

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (requiresStripeHold) {
        await holdPayment();
      } else if (isAdmin && !isCaptain) {
        await recordOffPlatformDeposit(
          bookingData.paymentId,
          selectedBoatDetails?.boatType,
          userData.userId
        );
      }
      const signatureResponses = await handleSaveSignatures();
      // await ensurePaymentCaptured();

      const updatedFormResponses = await new Promise((resolve) => {
        setFormResponses((prevResponses) => {
          const responses = signatureResponses.reduce(
            (currentResponses, signatureResponse) =>
              mergeUploadedResponse(currentResponses, signatureResponse),
            prevResponses
          );
          resolve(responses);
          return responses;
        });
      });

      const database = getDatabase();
      const tripDataRef = ref(
        database,
        `bookings/${dynamicId}/tripData/waivers/Captain's Waiver & Equipment Checklist`
      );

      const questionsArray = updatedFormResponses.map((response) => {
        let answer;
        if (response.type === "signature") {
          answer = response.Answer;
        } else if (response.type === "images") {
          // For images, use an array of URLs
          answer = response.Answer.map((image) => ({ url: image }));
        } else {
          // For other types, use the answer directly
          answer = response.Answer;
        }

        return {
          question:
            waiverQuestions.find((q) => q.id === response.Question)?.question ||
            response.Question,
          answer,
        };
      });

      await push(tripDataRef, {
        waiverName: "Captain's Waiver & Equipment Checklist",
        questions: questionsArray,
        signedAt: serverTimestamp(),
      });

      const bookingRef = ref(database, `bookings/${dynamicId}`);
      await update(bookingRef, { status: "tripStarted" });

      const tripData = ref(database, `bookings/${dynamicId}/tripData`);
      await update(tripData, { startTimestamp: serverTimestamp() });

      console.log("Form responses saved to Firebase:", questionsArray);

      const renterData = await getCurrentUserDetails();
      const renterName = renterData.firstName + " " + renterData.lastName;

      const emailBody = await tripStatusUpdateEmailBody(
        renterName,
        bookingData.listingTitle,
        "started"
      );
      sendEmail(renterData.email, "Trip Started", emailBody);

      const renterSmsBody = `Your trip of ${bookingData.listingTitle} has now started.`;
      const renterSmsResponse = await sendSMS(renterData.phone, renterSmsBody);
      console.log(renterSmsResponse);

      const ownerDetails = await getUserByID(bookingData.owner);
      console.log(ownerDetails, "new owner detials");
      const ownerName = ownerDetails?.firstName + " " + ownerDetails?.lastName;
      const emailOwnerBody = await tripStatusUpdateEmailBody(
        ownerName,
        bookingData.listingTitle,
        "started"
      );
      sendEmail(ownerDetails.email, "Trip Started", emailOwnerBody);

      const smsBody = `Trip of ${bookingData.listingTitle} with renter ${renterName} has now started.`;
      const smsResponse = await sendSMS(ownerDetails.phone, smsBody);
      console.log(dynamicId, "dynamicId");

      notifyOwnerTripStarted(
        bookingData.owner,
        bookingData.listingTitle,
        dynamicId,
        renterName
      ).catch((err) =>
        console.error("Failed to create owner notification:", err)
      );

      setLoading(false);
      navigate(`/dashboard/event/${dynamicId}`, {
        state: { showTripStartedGuidance: true },
      });
    } catch (error) {
      setLoading(false);
      console.error("Error submitting form:", error);
    }
  };

  // New function to ensure payment is captured
  const ensurePaymentCaptured = async () => {
    try {
      const database = getDatabase();
      const bookingSnapshot = await get(ref(database, `bookings/${dynamicId}`));
      const booking = bookingSnapshot.val();

      if (!booking || !booking.paymentId) {
        console.error("Booking or payment ID not found");
        return;
      }

      const paymentSnapshot = await get(
        ref(database, `payments/${booking.paymentId}`)
      );
      const payment = paymentSnapshot.val();

      if (!payment) {
        console.error("Payment record not found");
        return;
      }

      // If payment is still in 'authorized' state (not captured), capture it now
      if (payment.status === "authorized" && payment.stripePaymentIntentId) {
        if (!token) {
          throw new Error("Not authenticated");
        }

        // Call the backend to capture the payment
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/confirm-booking`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              bookingId: dynamicId,
            }),
          }
        );

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || "Failed to capture payment");
        }

        console.log("Payment successfully captured before trip start");
      }
    } catch (error) {
      console.error("Error ensuring payment is captured:", error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchWaiverData = async () => {
      try {
        const bookingData = await getBookingByID(dynamicId);
        if (!bookingData) return; // No need to proceed if bookingData is null

        let boatDetails = await getListingByID(bookingData?.listingId);
        setSelectedBoatDetails(boatDetails);
        console.log("bookingData:", { bookingData, boatDetails });
        setBookingData(bookingData);
        const inquiryData = await getInquiryDataById(bookingData.inquiryId);
        // console.log("inquiryData:", inquiryData);
        setInquiryDetails(inquiryData);
        setIsCaptain(inquiryData?.offerData?.captainCheckbox);

        const waiverName = `Captain's Waiver & Equipment Checklist`;

        const questions = await getWaiverDataByName(waiverName);
        const description = await getWaiverDescriptionByName(waiverName);

        setSigningWaiverName(waiverName);
        setWaiverDescription(description);

        const questionsWithId = questions.map((question, index) => ({
          ...question,
          id: index,
        }));
        const currentUserDetails = await getCurrentUserDetails();

        setWaiverQuestions(questionsWithId);
        setUserData(currentUserDetails);
        setIsAdmin(currentUserDetails?.role === "Admin");

        const listingData = await getListingByID(bookingData?.listingId);
        setListing(listingData);
        setBoatType(listingData?.boatType);
      } catch (error) {
        console.error("Error fetching waiver data:", error);
      }
    };

    fetchWaiverData();
  }, [dynamicId, bookingData?.listingId]);
  console.log(boatType, "boatType");
  useEffect(() => {
    const fetchListingDetails = async () => {
      try {
        const details = await getListingByID(bookingData?.listingId);
        setListing(details);
        console.log(details, "<------details");
      } catch (error) {
        console.error("Error fetching listing details:", error);
      }
    };

    fetchListingDetails();
  }, [bookingData?.listingId]);

  const securityAmount =
    selectedBoatDetails?.boatType?.toLowerCase() === "kayak"
      ? "$200"
      : selectedBoatDetails?.boatType?.toLowerCase() === "jetski"
      ? "$500"
      : "$800";

  console.log(waiverQuestions, "waiverQuestions");
  console.log(listing, "listing");

  return (
    <>
      <Modal
        centered
        show={showPreStartTripGuidanceModal}
        onHide={handleNotYetFromGuidance}
      >
        <Modal.Header closeButton>
          <Modal.Title>Ready to Start the Trip?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Only the driver of the boat completes this captain&apos;s waiver and
            equipment checklist. You&apos;ll need your credit card for the{" "}
            {securityAmount} security deposit hold. Click START to proceed or
            NOT YET to go back.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleNotYetFromGuidance}>
            Not yet
          </Button>
          <Button variant="primary" onClick={closePreStartTripGuidanceModal}>
            Start
          </Button>
        </Modal.Footer>
      </Modal>

      <Row
        className="card p-5 d-flex flex-row align-items-center"
        style={{ gap: "20px" }}
      >
        <Col xs={12} md={3}>
          <img
            className="img-fluid"
            src="/assets/backend/images/logo.png"
            style={{ backgroundColor: "#121212" }}
          ></img>
        </Col>
        <Col xs={12} md={9}>
          <h3
            className=""
            style={{ color: "#27313F", fontWeight: "800", textAlign: "center" }}
          >
            BBR BASS BOAT RENTALS, LLC
          </h3>
        </Col>
      </Row>

      <div className="row">
        <div className="col">
          <div className="page-description">
            <h1>{signingWaiverName}</h1>
            <div
              className="waiverDescrption mt-4"
              dangerouslySetInnerHTML={{ __html: waiverDescription }}
            />
          </div>
        </div>
      </div>
      <Row>
        <Col>
          <div className="card p-4">
            <form onSubmit={handleSubmit}>
              {waiverQuestions.map((questionData) => {
                if (
                  (boatType !== "Bass Boat" &&
                    questionData.showField === false) ||
                  (isCaptain === true && questionData.showField === false)
                ) {
                  return null;
                }
                return (
                  <div key={`${questionData.question}-${questionData.id}`}>
                    <label className="form-label form-label mt-2">
                      {questionData.question}:
                    </label>
                    <br />
                    {questionData.type === "text" ? (
                      <input
                        required={
                          questionData?.required &&
                          questionData?.required === "true"
                            ? true
                            : false
                        }
                        type="text"
                        className="form-control form-control"
                        value={
                          formResponses.find(
                            (response) => response.Question === questionData.id
                          )?.Answer || ""
                        }
                        onChange={(e) =>
                          handleInputChange(questionData.id, e.target.value)
                        }
                      />
                    ) : questionData.type === "images" ? (
                      <input
                        // required={
                        //   questionData?.required &&
                        //   questionData?.required === "true"
                        //     ? true
                        //     : false
                        // }
                        type="file"
                        accept="image/*"
                        capture="camera"
                        multiple
                        onChange={(e) =>
                          handleImageUpload(questionData.id, e.target.files)
                        }
                      />
                    ) : questionData.type === "date" ? (
                      <input
                        required={
                          questionData?.required &&
                          questionData?.required === "true"
                            ? true
                            : false
                        }
                        type="date"
                        className="form-control form-control"
                        value={
                          formResponses.find(
                            (response) => response.Question === "date"
                          )?.Answer || ""
                        }
                        onChange={(e) =>
                          handleInputChange("date", e.target.value)
                        }
                      />
                    ) : questionData.type === "country" ? (
                      <select
                        required={
                          questionData?.required &&
                          questionData?.required === "true"
                            ? true
                            : false
                        }
                        className="form-control"
                        name="settingsInputCountry"
                        value={
                          formResponses.find(
                            (response) => response.Question === "country"
                          )?.Answer || ""
                        }
                        onChange={(e) =>
                          handleInputChange("country", e.target.value)
                        }
                      >
                        <option>United States</option>
                        {/* Add other country options if needed */}
                      </select>
                    ) : questionData.type === "signature" ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
                        <SignatureCanvas
                          ref={(element) => {
                            if (element) {
                              signatureRefs.current[questionData.id] = element;
                            } else {
                              delete signatureRefs.current[questionData.id];
                            }
                          }}
                          onBegin={() => handleSignatureBegin(questionData.id)}
                          required={
                            questionData?.required &&
                            questionData?.required === "true"
                              ? true
                              : false
                          }
                          canvasProps={{
                            width: isMobile ? 250 : 500,
                            height: 200,
                            className: "sigCanvas",
                          }}
                        />
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px" }}>
                          <button
                            type="button"
                            onClick={() => {
                              signatureRefs.current[questionData.id]?.clear();
                              setSignatureDateTimes((prev) => {
                                const next = { ...prev };
                                delete next[questionData.id];
                                return next;
                              });
                            }}
                            className="btn btn-primary"
                          >
                            Clear
                          </button>
                          {signatureDateTimes[questionData.id] && (
                            <span style={{ fontSize: "13px", fontWeight: "600", color: "#27313F" }}>
                              ✅ Signed: {signatureDateTimes[questionData.id]}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      questionData.options.map((option) => (
                        <div
                          className="btn btn-light m-1 mb-4 form-check"
                          key={option}
                        >
                          <label htmlFor={option} className="form-check-label">
                            {option}
                          </label>
                          <input
                            required={
                              questionData?.required &&
                              questionData?.required === "true"
                                ? true
                                : false
                            }
                            type="radio"
                            name={questionData.id}
                            value={option}
                            id={option}
                            className="form-check-input"
                            checked={
                              formResponses.find(
                                (response) =>
                                  response.Question === questionData.id
                              )?.Answer === option
                            }
                            onChange={() =>
                              handleInputChange(questionData.id, option)
                            }
                          />
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
              {requiresStripeHold && (
                <Row className="mt-3">
                  <Col>
                    <h6>
                      A security amount of {securityAmount} will be withheld.
                    </h6>
                    <CardElement
                      className="form-control"
                      options={{
                        style: {
                          base: {
                            fontSize: "16px",
                            color: "#424770",
                            "::placeholder": {
                              color: "#aab7c4",
                            },
                          },
                          invalid: {
                            color: "#9e2146",
                          },
                        },
                      }}
                    />
                  </Col>
                </Row>
              )}

              {isAdmin && !isCaptain && (
                <Row className="mt-3">
                  <Col>
                    <Alert severity="info">
                      As an admin, you can start this trip without a credit card
                      hold. The renter should pay the {securityAmount} security
                      deposit directly to the boat owner (cash or Venmo).
                    </Alert>
                  </Col>
                </Row>
              )}

              {requiresStripeHold && (
                <Row className="mt-4">
                  <Col>
                    <div className="d-flex mt-1 additional-box">
                      <FormControlLabel
                        style={{ alignItems: "flex-start" }}
                        required
                        control={<Checkbox checked={acknowledgement} />}
                        onChange={setAcknowledgement}
                      />
                      <div>
                        <p style={{ fontWeight: "500", lineHeight: "28px" }}>
                          Please check this box to acknowledge that you will be
                          responsible for a {securityAmount} refundable security
                          fee <span style={{ color: "red" }}>*</span>
                        </p>
                        <p style={{ fontStyle: "italic", marginTop: "-11px" }}>
                          Will be held at the start of the trip and returned within 7 days from the trip end, if there are no claims by the boat owner
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              {isAdmin && !isCaptain && (
                <Row className="mt-4">
                  <Col>
                    <div className="d-flex mt-1 additional-box">
                      <FormControlLabel
                        style={{ alignItems: "flex-start" }}
                        required
                        control={<Checkbox checked={acknowledgement} />}
                        onChange={setAcknowledgement}
                      />
                      <div>
                        <p style={{ fontWeight: "500", lineHeight: "28px" }}>
                          I confirm the renter has paid or will pay the{" "}
                          {securityAmount} security deposit directly to the boat
                          owner <span style={{ color: "red" }}>*</span>
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
              {error && (
                <Alert className="mt-3" severity="error">
                  {error}
                </Alert>
              )}
              <button type="submit" className="btn btn-primary mt-3">
                {loading ? (
                  <ThreeDots
                    visible={true}
                    height="22"
                    width="22"
                    color="#fff"
                    radius="20"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                  />
                ) : (
                  "Submit Waiver"
                )}
              </button>
            </form>
          </div>
        </Col>
      </Row>
    </>
  );
}
